import React, { useEffect, useState, useRef } from 'react';
import css from './css/AddCustomQuestionForm.scss';
import commonCss from './css/SurveyStyle.scss';
import classnames from 'classnames';
import AddEditQuestionOptionModal from './AddEditQuestionOptionModal';
import QuestionOptionsTable from './QuestionOptionsTable';
import Select from 'common/StyledSelect';
import StyledButton from 'common/StyledButton';
import Icon from 'SF/MaterialIcon';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import useStarfishAPI from 'hooks/useStarfishAPI';

const AddCustomQuestionForm = ({formData, setNewQuestionData, setToastMessage, isOptionsQuestion, errors, updateErrors}) => {
    Modal.setAppElement(document.getElementById('main-center'));

    const [getQuestionTypesResponse, getQuestionTypesAPI] = useStarfishAPI({ isNewAPI: true });
    const [questionTypes, setQuestionTypes] = useState([]);
    //optionToEdit: will either be the option data the user wants to edit (ex. {text: "History", position: 0}),
    //or null if the user is adding a new option.
    const [optionToEdit, setOptionToEdit] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const addOptionRef = useRef();

    useEffect(() => {
        getQuestionTypesAPI('../api/student-survey-questions/question-types', { method: 'GET' });
    }, []);

    useEffect(() => {
        if (getQuestionTypesResponse.success) {
            setQuestionTypes(getQuestionTypesResponse.data);
        } else if (!getQuestionTypesResponse.pending && getQuestionTypesResponse.error) {
            updateErrors({formError: `There was an error processing your request. \
                If this error continues to appear, please contact Starfish support.`});
        }
    }, [getQuestionTypesResponse]);

    const closeModal = () => {
        setOptionToEdit(null);
        setIsModalOpen(false);
    };

    const replaceOption = (questionOptions, optionToReplace, newOptionText) =>
        questionOptions.map(option => ((option === optionToReplace) ? {text: newOptionText, position: option.position} : option))

    const saveOption = (newOptionText) => {
        const currentOptions = formData.questionOptions;
        const optionList = optionToEdit ? replaceOption(currentOptions, optionToEdit, newOptionText)
            : [...currentOptions,
                {
                    text: newOptionText,
                    position: currentOptions.length ? currentOptions.length : 0
                }
            ];
        setNewQuestionData({questionOptions: optionList});
        setToastMessage('Options updated. To finalize saving, select SAVE QUESTION.');
        updateErrors({questionOptionsError: null});
    };

    const editOption = (newEditOption) => {
        setOptionToEdit(newEditOption);
        setIsModalOpen(true);
    };

    const deleteOption = (newDeleteOption) => {
        const updatedOptions = formData.questionOptions.filter(option => option !== newDeleteOption)
            .map((option, index) => (
                {text: option.text, position: index}
        ));
        setNewQuestionData({questionOptions: updatedOptions});
        setToastMessage('Option removed. To finalize deletion, select SAVE QUESTION.');
        //For screen readers, focus on Add Option button after an option is deleted
        addOptionRef.current && addOptionRef.current.focus();
    };

    const fieldError = (message) => (
        <div className={commonCss['field-error']}><Icon icon='error' aria-hidden />{message}</div>
    );

    const getOptionsArea = () => (
        <div className={css['new-options']}>
            {formData.editable &&
                <>
                    <div className={classnames(commonCss['instructional-text'], commonCss['spacing-below'])}>
                        Add options in the order you wish them to display. Options can be edited or deleted but they
                        cannot be reordered.
                    </div>
                    <StyledButton ref={addOptionRef} type='button' inverted className={commonCss['styled-button']} onClick={() => setIsModalOpen(true)}>
                        <><Icon icon='add_circle' aria-hidden /> Add Option</>
                    </StyledButton>
                </>
            }
            <QuestionOptionsTable editable={formData.editable}
                                  options={formData.questionOptions}
                                  editOption={(option) => editOption(option)}
                                  deleteOption={(option) => deleteOption(option)} />
            {errors.questionOptionsError && fieldError(errors.questionOptionsError)}
        </div>
    );

    return (
        <>
            <form className={css['form']}>
                <div className={css['question-input']}>
                    <label htmlFor='question-text' className={classnames(commonCss['field-label'], {[commonCss['error']]: !!errors.questionTextError})}>
                        <span className={commonCss['required-asterisk']} aria-hidden>*</span>Question</label>
                     <div className={css['input-container']}>
                        {!formData.editable && <Icon icon='lock' className={css['lock-icon']} aria-hidden />}
                        <input
                            id='question-text'
                            className={classnames(css['input'], {[css['inputError']]: !!errors.questionTextError})}
                            type='text'
                            value={formData.questionText}
                            onChange={e => {
                                setNewQuestionData({questionText: e.target.value});
                                updateErrors({questionTextError: null});
                            }}
                            disabled={!formData.editable}
                            required
                        />
                    </div>
                    {errors.questionTextError && fieldError(errors.questionTextError)}
                </div>
                <div className={css['type-dropdown']}>
                    <label className={classnames(commonCss['field-label'], {[commonCss['error']]: !!errors.questionTypeError})}>
                        <span className={commonCss['required-asterisk']} aria-hidden>*</span>Question Type
                    </label>
                    <Select id='type-select'
                            aria-label='Question Type (Required)'
                            value={formData.questionType}
                            onChange={e => {
                                setNewQuestionData({questionType: e});
                                updateErrors({questionTypeError: null, questionOptionsError: null});
                            }}
                            options={questionTypes}
                            isLoading={getQuestionTypesResponse.pending}
                            hasError={!!errors.questionTypeError}
                            placeholder='Question Type'
                            isDisabled={!formData.editable} />
                    {errors.questionTypeError && fieldError(errors.questionTypeError)}
                </div>
                {isOptionsQuestion && getOptionsArea()}
            </form>
            <AddEditQuestionOptionModal isOpen={isModalOpen}
                                        closeModal={closeModal}
                                        currentOptions={formData.questionOptions}
                                        optionTextToEdit={optionToEdit?.text}
                                        isEdit={!!optionToEdit}
                                        onSave={(optionText) => saveOption(optionText)} />
        </>
    );
}

AddCustomQuestionForm.propTypes = {
    formData: PropTypes.shape({
        questionText: PropTypes.string.isRequired,
        questionType: PropTypes.object,
        questionOptions: PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string.isRequired,
            position: PropTypes.number.isRequired
        })).isRequired,
        editable: PropTypes.bool.isRequired
    }).isRequired,
    setNewQuestionData: PropTypes.func.isRequired,
    setToastMessage: PropTypes.func.isRequired,
    isOptionsQuestion: PropTypes.bool.isRequired,
    errors: PropTypes.shape({
        formError: PropTypes.string,
        questionTextError: PropTypes.string,
        questionTypeError: PropTypes.string,
        questionOptionsError: PropTypes.string
    }),
    updateErrors: PropTypes.func.isRequired
};

export default AddCustomQuestionForm;
